import { useMutation, useQuery } from '@apollo/client';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { Box, Button, Collapse, Container, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ConfigurePlanDocument } from '@/graphql/generated/configure-plan.generated';
import { PlanPermissionPricesDocument } from '@/graphql/generated/plan-permission-prices.generated';
import { PlansDocument } from '@/graphql/generated/plans.generated';
import { PermissionCode, PlanPriceInterval } from '@/graphql/generated/types.generated';
import { BillingIntervalToggle } from './BillingIntervalToggle';
import { BRAND_FEATURE_GROUPS, BRAND_FEATURED_CODES, FEATURED_CODES, FEATURE_GROUPS } from './constants';
import { calcTotalToPay } from './utils/calc-total-price.util';
import { FeatureGroupSection } from './FeatureGroupSection';
import { useCalculatorState } from './hooks/use-calculator-state';
import { PlanCard } from './PlanCard';
import { PriceSummary } from './PriceSummary';
import { AddOnRow } from './rows/AddOnRow';
import { mapGraphqlToCalculator } from './utils/map-graphql-to-calculator.util';
import { useSession } from '@/providers/session-provider';
import { isBrandPricingWorkspace, isSwonkieDevWorkspace } from '@/utils/users-path-filter';

interface PlanCalculatorProps {
    onSubmit: (priceRef: string) => unknown;
    initialPlanCode?: string;
    /** Billing interval to open on, e.g. the workspace's current one. Defaults to yearly. */
    initialInterval?: PlanPriceInterval;
    embedded?: boolean;
    /** Pre-set add-on values (code → value); clamped & validated before seeding. */
    initialAddOns?: Record<string, string | number | boolean>;
}

export const PlanCalculator = ({
    onSubmit,
    initialPlanCode,
    initialInterval,
    embedded,
    initialAddOns,
}: PlanCalculatorProps) => {
    const { workspace } = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const isDevWorkspace = isSwonkieDevWorkspace(workspace);
    const isBrandPricing = isBrandPricingWorkspace(workspace);
    const featureGroups = isBrandPricing ? BRAND_FEATURE_GROUPS : FEATURE_GROUPS;
    const featuredCodes = isBrandPricing ? BRAND_FEATURED_CODES : FEATURED_CODES;

    const { data: plansData, loading: plansLoading, error: plansError } = useQuery(PlansDocument);
    const { data: permData, loading: permLoading, error: permError } = useQuery(PlanPermissionPricesDocument);
    const [configurePlan, { loading: configuring }] = useMutation(ConfigurePlanDocument, {
        onError: (error) => enqueueSnackbar(t(error.message)),
    });

    const isLoading = plansLoading || permLoading || !!plansError || !!permError;

    const [interval, setInterval] = useState<PlanPriceInterval>(initialInterval ?? PlanPriceInterval.Year);
    const [showMorePlans, setShowMorePlans] = useState(false);

    // Re-derive mapped data whenever plans or permissions change.
    // interval is excluded — prices are always raw monthly; discount is applied at render/submit.
    const { plans: rawPlans } = useMemo(
        () => mapGraphqlToCalculator(permData?.planPermissionPrices ?? [], plansData?.plans ?? []),
        [permData, plansData],
    );

    // In brand mode, override the ProfilesGroups label so all renderers show "Brands".
    const plans = useMemo(() => {
        if (!isBrandPricing) return rawPlans;
        return rawPlans.map((plan) => ({
            ...plan,
            features: plan.features.map((f) =>
                f.code === PermissionCode.ProfilesGroups
                    ? {
                          ...f,
                          label: 'permission.profiles-groups.name-brand',
                          description: 'permission.profiles-groups.description-brand',
                      }
                    : f,
            ),
        }));
    }, [rawPlans, isBrandPricing]);

    const {
        selectedPlan,
        addOnQty,
        addOnBooleans,
        addOnEnums,
        viewMore,
        featuredFeatures,
        featureByCode,
        priceSummaryLines,
        activationOrder,
        handlePlanSelect,
        handleIntervalChange,
        handleAddOnChange,
        handleBoolChange,
        handleEnumChange,
        setViewMore,
    } = useCalculatorState({
        plans,
        onIntervalChange: setInterval,
        initialPlanCode,
        featuredCodes,
        initialAddOns,
    });

    const mainPlans = plans.slice(0, 3);
    const morePlans = plans.slice(3);

    // Each added brand includes 10 competitor profiles. The note reflects only the ADDITIONAL
    // competitors from the brands add-on (not the base plan's included brands).
    const COMPETITORS_PER_BRAND = 10;
    const additionalBrands = addOnQty[PermissionCode.ProfilesGroups] ?? 0;
    const additionalCompetitors = additionalBrands * COMPETITORS_PER_BRAND;
    const noteLine =
        isBrandPricing && additionalCompetitors > 0
            ? `+${additionalCompetitors} ${t('permission.competitors.name')}`
            : undefined;

    const handleGetStarted = async () => {
        if (!selectedPlan) return;

        const totalPrice = calcTotalToPay(selectedPlan, priceSummaryLines, interval);

        const enumFeatures = priceSummaryLines
            .filter(
                (line) =>
                    line.feature.addOnOptions &&
                    line.enumValue &&
                    line.enumValue !== '' &&
                    line.enumValue !== line.feature.displayValue,
            )
            .flatMap((line) => {
                const selected = line.feature.addOnOptions?.find((o) => o.value === line.enumValue);
                if (!selected) return [];
                return [{ id: selected.id, value: 1 }];
            });

        const features = [
            ...priceSummaryLines
                .filter((line) => line.extraQty > 0 || line.boolEnabled)
                .map((line) => ({
                    id: line.feature.id,
                    value: line.extraQty > 0 ? line.extraQty : line.boolEnabled ? 1 : 0,
                })),
            ...enumFeatures,
        ];

        const result = await configurePlan({
            variables: { planId: selectedPlan.id, features, totalPrice, interval },
        });

        const priceRef = result.data?.configurePlan;
        if (!priceRef) {
            return;
        }

        onSubmit(priceRef);
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
                <Stack spacing={4}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            gap: 3,
                        }}
                    >
                        {[0, 1, 2].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                        ))}
                    </Box>
                </Stack>
            </Container>
        );
    }

    if (isDevWorkspace) return null;

    if (!selectedPlan) return null;

    return (
        <Container maxWidth="lg" sx={embedded ? undefined : { py: { xs: 4, md: 8 } }}>
            <Stack spacing={3}>
                {/* ── Page header ──────────────── */}

                <Box textAlign="center">
                    <Typography variant="h4" fontWeight={800} gutterBottom>
                        {t('billing.calculator.header.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" maxWidth={520} mx="auto">
                        {t('billing.calculator.header.subtitle')}
                    </Typography>
                </Box>

                {/* ── Billing interval toggle ───────────────────────────── */}
                <BillingIntervalToggle value={interval} onChange={handleIntervalChange} />

                {/* ── Two-column layout: config left, sticky summary right ── */}
                <Grid container spacing={4} alignItems="flex-start">
                    {/* Left: plan cards + customisation */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Stack spacing={4}>
                            {/* Plan cards */}
                            <Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, minmax(0, 1fr))',
                                            md: 'repeat(3, minmax(0, 1fr))',
                                        },
                                        gap: 2,
                                    }}
                                >
                                    {mainPlans.map((plan) => (
                                        <PlanCard
                                            key={plan.id}
                                            plan={plan}
                                            interval={interval}
                                            selected={selectedPlan.code === plan.code}
                                            onSelect={handlePlanSelect}
                                            featuredCodes={featuredCodes}
                                        />
                                    ))}
                                </Box>

                                {morePlans.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2 }}>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                endIcon={
                                                    showMorePlans ? (
                                                        <ExpandLessRounded />
                                                    ) : (
                                                        <ExpandMoreRounded />
                                                    )
                                                }
                                                onClick={() => setShowMorePlans((v) => !v)}
                                            >
                                                {showMorePlans
                                                    ? t('billing.pricing.modal.subcription.see-less')
                                                    : t('billing.pricing.modal.subcription.see-more')}
                                            </Button>
                                        </Divider>

                                        <Collapse in={showMorePlans}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: {
                                                        xs: '1fr',
                                                        sm: 'repeat(2, minmax(0, 1fr))',
                                                        md: 'repeat(3, minmax(0, 1fr))',
                                                    },
                                                    gap: 2,
                                                }}
                                            >
                                                {morePlans.map((plan) => (
                                                    <PlanCard
                                                        key={plan.id}
                                                        plan={plan}
                                                        interval={interval}
                                                        selected={selectedPlan.code === plan.code}
                                                        onSelect={handlePlanSelect}
                                                        featuredCodes={featuredCodes}
                                                    />
                                                ))}
                                            </Box>
                                        </Collapse>
                                    </>
                                )}
                            </Box>

                            {/* Customisation section */}
                            <Box>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {t('billing.calculator.customize.title', { plan: selectedPlan.name })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={3}>
                                    {t('billing.calculator.customize.subtitle')}
                                </Typography>

                                {/* Featured add-ons */}
                                <Stack spacing={1.5}>
                                    {featuredFeatures.map((feature) => (
                                        <AddOnRow
                                            key={feature.code}
                                            feature={feature}
                                            extraQty={addOnQty[feature.code] ?? 0}
                                            currency={selectedPlan.currency}
                                            onChange={handleAddOnChange}
                                        />
                                    ))}
                                </Stack>

                                {/* View more */}
                                <Box mt={2}>
                                    {featureGroups.length > 0 && (
                                        <Divider sx={{ my: 2 }}>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                endIcon={
                                                    viewMore ? <ExpandLessRounded /> : <ExpandMoreRounded />
                                                }
                                                onClick={() => setViewMore((v) => !v)}
                                            >
                                                {viewMore
                                                    ? t('billing.calculator.customize.show-fewer')
                                                    : t('billing.calculator.customize.view-all')}
                                            </Button>
                                        </Divider>
                                    )}

                                    <Collapse in={viewMore}>
                                        <Stack spacing={3} mt={2}>
                                            {featureGroups.map((group) => (
                                                <FeatureGroupSection
                                                    key={group.label}
                                                    group={group}
                                                    featureByCode={featureByCode}
                                                    currency={selectedPlan.currency}
                                                    addOnQty={addOnQty}
                                                    addOnBooleans={addOnBooleans}
                                                    addOnEnums={addOnEnums}
                                                    onNumericChange={handleAddOnChange}
                                                    onBoolChange={handleBoolChange}
                                                    onEnumChange={handleEnumChange}
                                                />
                                            ))}
                                        </Stack>
                                    </Collapse>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Right: sticky price summary */}
                    <Grid
                        size={{ xs: 12, md: 4 }}
                        sx={{ position: { xs: 'static', md: 'sticky' }, top: 24, alignSelf: 'flex-start' }}
                    >
                        <PriceSummary
                            plan={selectedPlan}
                            addOnLines={priceSummaryLines}
                            interval={interval}
                            activationOrder={activationOrder}
                            onGetStarted={handleGetStarted}
                            note={noteLine}
                            loading={configuring}
                        />
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    );
};
